// Confirmation-only celebration: subtle motion + synthesized Web Audio cues.
// Sounds fire ONLY on real user-confirmed actions (a user gesture), respect a
// Sound On/Off preference and reduced-motion, and are short and restrained.

const SOUND_KEY = "ms_sound_enabled";

export function soundEnabled() {
  try { return localStorage.getItem(SOUND_KEY) !== "off"; } catch { return true; }
}
export function setSoundEnabled(on) {
  try { localStorage.setItem(SOUND_KEY, on ? "on" : "off"); } catch {}
}
function reduceMotion() {
  try { return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

let ctx;
function audio() {
  if (!soundEnabled()) return null;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = ctx || new AC();
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  } catch { return null; }
}

// A short, clean note. type: sine/triangle. gain kept low and quick to decay.
function note(freq, start, dur, peak = 0.12, type = "sine") {
  const ac = ctx; if (!ac) return;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type; osc.frequency.value = freq;
  osc.connect(g); g.connect(ac.destination);
  const t = ac.currentTime + start;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.start(t); osc.stop(t + dur + 0.02);
}

// Tasteful cues, never casino-like.
const CUES = {
  chime: () => { note(880, 0, 0.5, 0.1); note(1320, 0.06, 0.5, 0.07); },             // protection
  cash: () => { note(660, 0, 0.3, 0.1); note(990, 0.08, 0.35, 0.08); },             // debt deployed
  fanfare: () => { [523, 659, 784, 1047].forEach((f, i) => note(f, i * 0.1, 0.5, 0.11, "triangle")); }, // debt eliminated
  victory: () => { [523, 659, 784, 1047, 1319].forEach((f, i) => note(f, i * 0.11, 0.6, 0.12, "triangle")); }, // all debt gone
  success: () => { note(784, 0, 0.45, 0.1); note(1175, 0.07, 0.45, 0.07); },        // runway / goal complete
};

function confetti(level) {
  if (reduceMotion()) return;
  const n = level === "major" ? 90 : level === "medium" ? 48 : 22;
  const colors = ["#2F6B57", "#C9A86A", "#164C3B", "#DDE7DF", "#9A7B3A"];
  const root = document.createElement("div");
  root.setAttribute("aria-hidden", "true");
  root.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
  document.body.appendChild(root);
  for (let i = 0; i < n; i++) {
    const p = document.createElement("div");
    const size = 6 + Math.random() * 6;
    const left = Math.random() * 100;
    const dur = 1600 + Math.random() * 1400;
    const delay = Math.random() * 200;
    const rot = Math.random() * 360;
    p.style.cssText = `position:absolute;top:-20px;left:${left}vw;width:${size}px;height:${size * 0.5}px;background:${colors[i % colors.length]};opacity:0.9;border-radius:1px;transform:rotate(${rot}deg);animation:ms-confetti ${dur}ms cubic-bezier(.2,.6,.3,1) ${delay}ms forwards`;
    root.appendChild(p);
  }
  setTimeout(() => root.remove(), 3400);
}

if (typeof document !== "undefined" && !document.getElementById("ms-confetti-style")) {
  const st = document.createElement("style");
  st.id = "ms-confetti-style";
  st.textContent = "@keyframes ms-confetti{to{transform:translateY(105vh) rotate(720deg);opacity:0}}";
  document.head.appendChild(st);
}

// Public API. event: protection | debt | debt_eliminated | all_debt_gone | runway_complete | goal
export function celebrate(event) {
  audio();
  switch (event) {
    case "protection": CUES.chime?.(); confetti("small"); break;
    case "debt": CUES.cash?.(); confetti("small"); break;
    case "debt_eliminated": CUES.fanfare?.(); confetti("medium"); break;
    case "all_debt_gone": CUES.victory?.(); confetti("major"); break;
    case "runway_complete": CUES.success?.(); confetti("medium"); break;
    case "goal": CUES.success?.(); confetti("small"); break;
    case "goal_complete": CUES.victory?.(); confetti("major"); break;
    default: break;
  }
}
